/**
 * The 3D icon drawn in place of a "Why people go" row's emoji (owner,
 * 2026-10-25). Editors still write an emoji (`whyGoItemSchema.emoji`); this
 * table turns it into an icon key. Web serves `/images/why-go/<key>.webp`, the
 * app bundles the same set from `@majourneys/assets` (`whyGoIcons`). An emoji
 * with no entry keeps rendering as itself, so a new one never breaks a row.
 *
 * Keys are written without the U+FE0F variation selector, which editors and
 * keyboards add or drop at random (🏛 vs 🏛️); `whyGoIconKey` strips it too.
 */
const WHY_GO_ICON_BY_EMOJI: Record<string, string> = {
  // Food and cuisine
  // 🍲 is the tagine pot; a tanjia row takes 🏺, the clay jar (owner, 2026-10-26).
  "🍲": "stew-pot",
  "🏺": "tanjia",
  "🍣": "sushi",
  "🥘": "shallow-pan",
  "🍤": "shrimp",
  "🍽": "plate",
  "🥙": "flatbread",
  "🍕": "pizza",
  "🐟": "fish",
  "🥖": "baguette",
  "🍰": "cake",
  "🌶": "chili",
  "🍝": "pasta",
  "🍔": "burger",
  "🦪": "oyster",
  "🥩": "steak",
  // Moroccan and world food library (owner's ChatGPT set, 2026-10-28)
  "🍵": "mint-tea",
  "🫖": "teapot",
  "🥟": "briouat",
  "🥧": "pastilla",
  "🍢": "brochettes",
  "🍖": "mechoui",
  "🥣": "harira",
  "🫓": "msemen",
  "🍯": "honey",
  "🌴": "dates",
  "🍊": "orange-juice",
  "🧆": "couscous",
  "🫕": "rfissa",
  "🍪": "moroccan-sweets",
  "🥮": "chebakia",
  "🧂": "spices",
  "🌸": "saffron",
  "🥜": "amlou",
  "🫙": "argan-oil",
  "🥐": "breakfast",
  "🍳": "brunch",
  "☕": "coffee",
  "🧁": "cupcake",
  "🍦": "ice-cream",
  "🍫": "chocolate",
  "🥗": "salad",
  "🌮": "tacos",
  "🍜": "ramen",
  "🥢": "asian",
  "🥞": "pancakes",
  "🧇": "waffle",
  "🍗": "chicken",
  // Kitchen and fire
  "🔥": "flame",
  "👨‍🍳": "chef",
  "👩‍🍳": "chef",
  "🧑‍🍳": "chef",
  "💚": "pistachio",
  // Music and shows
  "🎶": "music-notes",
  "🎵": "music-notes",
  "🎤": "microphone",
  "💃": "dancer",
  "🎭": "masks",
  "🥁": "drum",
  "🎩": "top-hat",
  "🎸": "guitar",
  "🎧": "headphones",
  "🎹": "piano",
  "🎉": "party",
  "🎊": "party",
  "🎰": "casino",
  // Setting and decor
  "🌿": "terrace-plant",
  "🪴": "terrace-plant",
  "🏛": "palace",
  "🌇": "rooftop",
  "🌳": "garden",
  "🐆": "panther",
  "🏮": "lantern",
  "🫒": "olive",
  "🚪": "hidden-door",
  "🌹": "rose",
  "🏘": "street",
  "🖼": "frame",
  "⛳": "golf",
  // Place and time
  "📍": "pin",
  // 🕌 alone is the Koutoubia minaret (Marrakech). The Hassan II Mosque on
  // the Casablanca seafront has its own picture, written as wave + mosque
  // (owner, 2026-11-04), so a row that falls back to text still reads right.
  "🕌": "mosque",
  "🌊🕌": "hassan-ii-mosque",
  // 🌆 is Jemaa El-Fna: the square, its stalls and the Koutoubia (owner, 2026-11-03).
  "🌆": "jemaa-el-fna",
  "🕛": "clock",
  "🕰": "clock",
  "⏰": "clock",
  "🌅": "sunset",
  "🌙": "moon",
};

/** Every icon key with a file behind it, for asset tables and tests. */
export const WHY_GO_ICON_KEYS: readonly string[] = [...new Set(Object.values(WHY_GO_ICON_BY_EMOJI))].sort();

/** The icon key for a row's emoji, or null when it has no 3D icon yet. */
export function whyGoIconKey(emoji: string | null | undefined): string | null {
  if (!emoji) return null;
  return WHY_GO_ICON_BY_EMOJI[emoji.trim().replace(/\uFE0F/g, "")] ?? null;
}
